// Web copy of docs/privacy-policy.md (keep in sync with it and ios/.../PrivacyPolicyView.swift).
// Also served publicly at /privacy.
import { PRIVACY_EMAIL } from '../config.ts';

export function PrivacyPolicy() {
  return (
    <article className="stack policy">
      <h1>Privacy policy</h1>
      <p className="caption">Draft for the pilot, version 2026-10-17.</p>
      <h2>Who we are</h2>
      <p>Recover is a training log for people who train with a personal trainer. Contact: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.</p>
      <h2>What we collect</h2>
      <ul>
        <li>From the app: your name, chosen body model, calendar keyword and time zone; the muscles you log; intensity and recovery ratings; daily recovery check-ins; optional notes.</li>
        <li>From Apple Health (iPhone app, with your permission): per-workout summaries only (activity type, start and end, duration, average and maximum heart rate, active energy, distance).</li>
        <li>From your calendar (iPhone app, with your permission): the start and end times of events that match your training keyword.</li>
        <li>From the web app: your email address (to sign in with a one-time code), and the dates and times of upcoming trainer sessions you enter.</li>
        <li>From trainers: notes they send about you through WhatsApp, and their WhatsApp number and name.</li>
      </ul>
      <p>We do not collect raw heart-rate samples, routes, location, sleep, other Health data, calendar events that don't match your keyword, or your Apple ID email.</p>
      <h2>Why</h2>
      <p>To show your recent muscle load and recovery, remind you to log sessions, and, only if you link a trainer and allow it, send that trainer a digest before your sessions.</p>
      <h2>Your trainer</h2>
      <p>Trainers have no access to the app. They receive WhatsApp messages filtered by your sharing switches (muscles, ratings, solo workouts, Health details) and can send notes about you. You can always read those notes in the app. You can disconnect a trainer or turn sharing off at any time.</p>
      <h2>Apple Health</h2>
      <p>Health data is used only to provide the app's features. It is never used for advertising or data mining, never sold, and never shared with third parties other than your linked trainer as described above.</p>
      <h2>Processors</h2>
      <ul>
        <li>Supabase: database and authentication, EU region.</li>
        <li>Twilio (delivers WhatsApp messages; WhatsApp is operated by Meta): delivers messages to trainers.</li>
        <li>Cloudflare: hosts the Recover web app (serves the pages; it does not receive your training data).</li>
        <li>Anthropic: reads the text of trainer notes to work out which trainee they are about. Only the trainer's message and their trainees' names are sent, never health data.</li>
      </ul>
      <h2>Security</h2>
      <p>Data is encrypted in transit (TLS) and at rest. Row-level security means only you can read your data. On the iPhone, cached data uses complete file protection.</p>
      <h2>Retention and deletion</h2>
      <p>Data is kept while your account exists. You → Delete my account and data removes everything immediately: your profile, sessions, ratings, check-ins, consents, trainer links, notes about you, and digests logged by the bot.</p>
      <h2>Your rights</h2>
      <p>You may access, correct, export or delete your data and withdraw consent at any time, in the app or by email.</p>
    </article>
  );
}

export function DataInventory() {
  return (
    <article className="stack policy">
      <h1>What we store</h1>
      <h2>On our servers (EU, encrypted at rest)</h2>
      <ul>
        <li>Your email address (sign-in), name, chosen body model, time zone</li>
        <li>Sessions: date, type, muscles you selected, intensity and recovery ratings, your note</li>
        <li>Daily recovery check-ins</li>
        <li>Times of upcoming trainer sessions you enter (to time the trainer digest)</li>
        <li>Trainer links, your sharing choices, and notes trainers send about you</li>
      </ul>
      <h2>Never stored</h2>
      <ul><li>Location, heart-rate samples or any other health data beyond what you enter</li></ul>
      <h2>Shared with your trainer only if you allow it</h2>
      <ul><li>A WhatsApp digest before each session, filtered by your sharing switches</li></ul>
    </article>
  );
}

export function Credits() {
  return (
    <article className="stack policy">
      <h1>Credits</h1>
      <h2>Anatomy</h2>
      <p>Body models are derived from BodyParts3D, © The Database Center for Life Science, licensed under CC BY 4.0
        (<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">creativecommons.org/licenses/by/4.0</a>).
        Accessed via the human-atlas project (MIT). Recover segmented the skin surface into muscle zones, synthesised lats and abs zones,
        smoothed the head into a mannequin, created the female shape and traced the 2D front and back views; these changes are ours.</p>
      <p className="caption">Muscle zones approximate the underlying anatomy for training logs and are not a medical reference.</p>
    </article>
  );
}
